import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { juntarClasses } from './classes';
import { IconeEspiral } from './icones';

export type VarianteBotao = 'primario' | 'secundario' | 'sutil' | 'perigo';
export type TamanhoBotao = 'pequeno' | 'medio' | 'grande';

/**
 * Cada variante define fundo, texto e borda nos dois temas usando só tokens.
 *
 * O primário usa índigo, não ciano: no tema claro o ciano do token
 * (`8 145 178`) rende 3,55:1 contra a superfície base — abaixo do mínimo de
 * 4,5:1 para texto. O índigo passa nos dois temas. A correção do token de ciano
 * está registrada na INTEGRACAO.md; o ciano segue no anel de foco e em traços,
 * onde 3:1 basta.
 */
const VARIANTES: Readonly<Record<VarianteBotao, string>> = {
  primario:
    'bg-acento-indigo text-superficie-base hover:bg-acento-indigo/90 active:bg-acento-indigo/80',
  secundario:
    'border border-borda bg-superficie-elevada text-conteudo-primario hover:border-acento-ciano/50 hover:bg-superficie-vidro',
  sutil: 'text-conteudo-secundario hover:bg-superficie-vidro hover:text-conteudo-primario',
  perigo: 'bg-erro text-superficie-base hover:bg-erro/90 active:bg-erro/80',
};

/**
 * Alturas em múltiplos de 4 (grid de 8pt). `medio` tem 44px — o alvo de toque
 * recomendado. `pequeno` (36px) é para barra de ferramentas densa, onde o alvo
 * fica cercado de espaçamento; não use em tela de toque como ação principal.
 */
const TAMANHOS: Readonly<Record<TamanhoBotao, string>> = {
  pequeno: 'h-9 gap-1.5 px-3 text-xs',
  medio: 'h-11 gap-2 px-4 text-sm',
  grande: 'h-12 gap-2 px-6 text-base',
};

const BASE =
  'inline-flex select-none items-center justify-center whitespace-nowrap rounded-card font-medium tracking-tight transition-colors duration-150 disabled:pointer-events-none disabled:opacity-50';

export interface PropsBotao extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variante?: VarianteBotao;
  tamanho?: TamanhoBotao;
  /** Ocupa toda a largura disponível. */
  larguraTotal?: boolean;
  /** Desabilita e mostra a espiral. O rótulo permanece — a largura não pula. */
  carregando?: boolean;
  /** Texto anunciado a leitor de tela enquanto carrega. */
  rotuloDeCarregamento?: string;
  iconeInicial?: ReactNode;
  iconeFinal?: ReactNode;
  /** Só para layout (margem, largura). Cor e altura vêm da variante. */
  className?: string;
}

/**
 * Classes do botão, para estilizar um `<a>` ou um `<Link>` do router com a mesma
 * aparência sem aninhar âncora dentro de botão.
 */
export function classesDeBotao(
  variante: VarianteBotao = 'primario',
  tamanho: TamanhoBotao = 'medio',
  className?: string,
): string {
  return juntarClasses(BASE, VARIANTES[variante], TAMANHOS[tamanho], className);
}

export const Botao = forwardRef<HTMLButtonElement, PropsBotao>(function Botao(
  {
    variante = 'primario',
    tamanho = 'medio',
    larguraTotal = false,
    carregando = false,
    rotuloDeCarregamento = 'Carregando',
    iconeInicial,
    iconeFinal,
    className,
    children,
    disabled,
    type = 'button',
    ...resto
  },
  referencia,
): JSX.Element {
  return (
    <button
      ref={referencia}
      // O legado tinha botões sem `type` dentro de formulário, que submetiam sem
      // querer. Aqui o padrão é `button`; submissão é explícita.
      type={type}
      disabled={disabled === true || carregando}
      aria-busy={carregando || undefined}
      className={juntarClasses(
        classesDeBotao(variante, tamanho),
        larguraTotal && 'w-full',
        className,
      )}
      {...resto}
    >
      {carregando ? <IconeEspiral /> : iconeInicial}
      {children}
      {carregando ? <span className="sr-only">{rotuloDeCarregamento}</span> : iconeFinal}
    </button>
  );
});
